/**
 * Wire + view-model types for the xplatform mapping service job APIs.
 * Wire types mirror `xplatform_mapping_service/docs/APIs.md`.
 * ViewModel types are the camelCase shapes consumed by the React UI.
 * Convert at the boundary (mappingApi / notificationApi) using the
 * builders under `src/utils/builders/`.
 */

import type { TmVariant } from "./mappingResult";

export const JobStatus = {
  AwaitingUpload: "AwaitingUpload",
  Parsing: "Parsing",
  Running: "Running",
  Completed: "Completed",
  CompletedWithErrors: "CompletedWithErrors",
  Failed: "Failed",
} as const;

export type JobStatus = (typeof JobStatus)[keyof typeof JobStatus];

export type JobInputKind = "text" | "json" | "csv" | "excel" | "zip";
export type JobSourceKind = "single" | "zip" | "batch";

// ---------- Wire types (snake_case, server-shaped) ---------------------

export interface CreateJobRequest {
  service: string;
  plugin_id: string;
  input_kind: JobInputKind;
  filename: string;
  /** Job-wide overrides; ZIP entries are matched by their full member path. */
  options?: Record<string, unknown>;
  files?: { name: string; options: Record<string, unknown> }[];
}

export interface CreateJobResponse {
  job_id: string;
  status: JobStatus;
  upload_url: string;
  upload_content_type: string;
  upload_expires_at: string;
  max_bytes: number;
}

export interface JobError {
  error_code: string;
  message: string;
  description?: string;
}

/** One input file, irrespective of upload, ZIP membership, or extraction origin. */
export interface JobFile {
  file_index: number;
  status: "Pending" | "Running" | "Completed" | "Failed";
  input_kind: JobInputKind;
  display_name: string;
  member_path?: string | null;
  origin?: { kind: "upload" | "zip_member" | "extraction_process_item"; ref: string } | null;
  has_input_pdf: boolean;
  error?: JobError | null;
  source_units?: { kind: string; total: number; done: number } | null;
  rows_total: number;
  rows_ok: number;
  rows_failed: number;
  rows_no_match: number;
  accepted_count: number;
}

export interface JobFilesResponse {
  files: JobFile[];
}

export interface JobStatusResponse {
  job_id: string;
  process_id?: string | null;
  plugin_id: string;
  status: JobStatus;
  input_kind: JobSourceKind;
  filename: string;
  file_count: number;
  files_done: number;
  failed_files: number;
  rows_total: number | null;
  rows_ok: number;
  rows_failed: number;
  rows_no_match: number;
  accepted_count: number;
  error: JobError | null;
  created_at: string;
  updated_at: string;
}

export interface OriginalFileResponse {
  download_url: string;
  filename: string;
}

/** Creator identity retained after completion; email is not persisted. */
export interface JobOwner {
  user_id: string | null;
  first_name: string | null;
  last_name: string | null;
}

export interface JobListItem {
  job_id: string;
  plugin_id: string;
  service: string;
  status: JobStatus;
  input_kind: JobSourceKind;
  filename: string;
  file_count: number;
  files_done: number;
  failed_files: number;
  rows_total: number | null;
  rows_ok: number;
  rows_failed: number;
  rows_no_match: number;
  error: JobError | null;
  created_by: JobOwner | null;
  created_at: string;
  updated_at: string;
}

export interface JobListResponse {
  jobs: JobListItem[];
  cursor: string | null;
}

/** Terminal job event — `write_result` publishes one per job. */
export interface MappingJobTerminalWebSocketMessage {
  type: "mapping.job.completed" | "mapping.job.failed";
  job_id: string;
  plugin_id: string;
  status: JobStatus;
  result_url_path: string;
}

/** Per-file processing progress while a job is running. */
export interface MappingJobProgressWebSocketMessage {
  type: "mapping.job.progress";
  job_id: string;
  status: JobStatus;
  file_index?: number;
  source_units?: { kind: string; total: number; done: number };
  rows_total: number;
  rows_ok: number;
  rows_failed?: number;
}

export type MappingJobWebSocketMessage =
  | MappingJobTerminalWebSocketMessage
  | MappingJobProgressWebSocketMessage;

/**
 * Per-hit metadata the backend writer attaches. Known scoring/context
 * fields are typed; index-side fields (item_mp_text, value_display,
 * unit_en, …) and anything new fall through the index signature.
 * Mirrors what `execute_row` puts in `VectorMatchHit.metadata`.
 */
/** Provenance of the past confirmation a similarity auto-accept replayed. */
export interface AutoAcceptedFrom {
  query_text?: string;
  source_job_id?: string;
  source_row_index?: number;
  accepted_by?: string;
  accepted_at?: string;
  confirm_count?: number;
}

/** One past confirmation for a similar input — source → mapped target. */
export interface SimilarHistoryEntry {
  similarity: number;
  source: {
    path?: string | null;
    value?: string | null;
    category?: string | null;
  };
  mapped: {
    item_mp_id?: string | null;
    item_mp_text?: string | null;
    value_display?: string | null;
    main_group?: string | null;
    sub_group?: string | null;
  };
  target_key?: string;
  accepted_by?: string | null;
  accepted_at?: string | null;
  confirm_count?: number;
  source_job_id?: string | null;
  source_row_index?: number | null;
}

export interface MatchMetadata {
  /** Confidence band of the row this hit belongs to — high | medium | low. */
  confidence_band?: string;
  /** Raw semantic similarity in [0, 1] before golden-example boosting.
   *  Equal to the top-level ``similarity`` for normal hits, so the TM
   *  response omits it as a duplicate; other services still send it. */
  semantic_score?: number;
  /** Score after augmentation (similarity-scaled, [0, 1]). */
  composite_score?: number;
  /** The "why this match" explanation — LLM rerank pick or the auto-accept reason. */
  rerank_reason?: string;
  /** True when the hit fell inside the LLM-suggested category. */
  in_category?: boolean;
  /** "golden_example" for synthesised hits; "golden_similar" for an
   *  auto-accepted similarity cache hit. */
  source?: string;
  /** True when this row was auto-accepted from a near-identical past confirmation. */
  auto_accept?: boolean;
  /** Provenance of the confirmation a similarity auto-accept replayed from. */
  auto_accepted_from?: AutoAcceptedFrom;
  /** Up to N golden examples for similar inputs. For TM this is the top-5 from
   *  the pre-clean lookup on the raw path+value (rendered by the score
   *  info-icon "accepted a similar example before" tooltip); for wd it's the
   *  post-ANN lookup. The single field for both — no separate
   *  example_candidates. */
  similar_history?: SimilarHistoryEntry[];
  /** TM only — the concrete value's own id. Each (item_mp_id, value_id) is a
   *  distinct candidate row: the same leaf with two different values is two
   *  separate rows/alternatives (no grouping by item_mp_id). */
  value_id?: string | number;
  /** TM only — the concrete DB value this hit maps to, e.g. "every 5,000 km".
   *  Rendered in the "Value" column. */
  value_display?: string;
  /** TM only — canonical QA-template row label, e.g.
   *  "2646 - Engine oil specification, [2-Adjustment data, ...]".
   *  Rendered as the "Item-MP Listing" column. */
  item_mp_listing?: string;
  /** TM only — true when this hit's item_mp_id is not in the QA template
   *  allow-list (the top match drives the row's "outside" status). */
  outside_template?: boolean;
  /** WD only — this hit is a proposal for a catalogue entry that does not
   * exist. The row reports `no_match`, whereas this flag describes the match;
   * it becomes false when a reviewer chooses an existing candidate. */
  llm_suggested?: boolean;
  /** wd only — the catalogue name the select LLM proposes for the missing
   *  entry. An AI suggestion, never a stored record. */
  proposed_text?: string;
  readonly [k: string]: unknown;
}

export interface MatchInfo {
  rank: number;
  key: string;
  distance: number;
  /** Normalized similarity in [0, 1] computed server-side from
   *  ``distance`` using the plugin's ``distance_metric``. Higher is
   *  better. Optional for back-compat with older results. */
  similarity?: number | null;
  /** Headline 0-100 review score. Server-side blend of the hit's
   *  ``composite_score`` (similarity, after any golden/BM25 boost), the
   *  rerank confidence, and — for plugins that enable it — a canonical
   *  bonus, each with a per-plugin weight. ``category_score`` is NOT part
   *  of this number. Optional for rows written before the field existed;
   *  the UI falls back to ``similarity`` / ``distance``. */
  score?: number | null;
  /** The 'key' half of the match — 0-100 agreement between the row's
   *  suggested domain/main_group and this hit's taxonomy position. null
   *  when there is no category signal to judge. */
  category_score?: number | null;
  metadata: MatchMetadata;
}

export interface RowContext {
  label?: string;
  page?: number;
  bbox?: [number, number, number, number];
  conf?: number;
  [k: string]: unknown;
}

export interface ResultRow {
  row_index: number;
  review_status: ReviewStatus;
  /** What the PIPELINE concluded about this row, independent of what a
   *  reviewer did. The catalogue-miss axis: the old `is_outside` / `not_exist`
   *  booleans were folded into it and are no longer sent. Confidence stays on
   *  `confidence_band`. Optional only so rows cached from before the change
   *  still parse. */
  mapping_status?: MappingStatus;
  plugin_id: string;
  model_name: string;
  kind: string;
  text: string;
  context: RowContext | null;
  top_match: MatchInfo | null;
  alternatives: MatchInfo[];
  /** Number of alternative matches available for this row. TM ships only
   *  this count in the list and serves the alternatives themselves on
   *  demand from `GET .../results/{row_index}/alternatives`; other services
   *  still inline `alternatives`, so this is optional. */
  alt_count?: number;
  /** True when the row's effective match has golden "similar history"
   *  examples. TM ships only this flag in the list and serves the examples
   *  on demand from `GET .../results/{row_index}/examples`. */
  has_examples?: boolean;
  chosen_rank?: number | null;
  error: JobError | null;
  /** Confidence band of the top match — high | medium | low. null for
   *  failed rows or rows written before the confidence gate existed. */
  confidence_band?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Per-plugin review-UI renderer instructions.
 *
 * Backend resolves the active plugin config and attaches the
 * ``display`` block to every /jobs/{id}/result page so the FE renders
 * plugin-specific headlines, breadcrumbs, and expanded-row columns
 * without a second fetch. ``null`` (or missing fields) → FE falls
 * back to its convention-based renderer.
 */
export type AltColumnRender = "text" | "id" | "monospace" | "score";

export interface AltColumn {
  metadata_key: string;
  label: string;
  width_pct: number;
  render: AltColumnRender;
}

export interface DisplayConfig {
  headline_fields: string[];
  headline_separator: string;
  identity_field: string | null;
  breadcrumb_fields: string[];
  alt_columns: AltColumn[];
}

/** Body of `GET /jobs/{job_id}/results/{row_index}/examples` — the golden
 *  "accepted a similar example before" history for one match of a row,
 *  fetched when the reviewer opens the score info-popup. */
export interface RowExamplesResponse {
  job_id: string;
  row_index: number;
  rank: number;
  examples: SimilarHistoryEntry[];
}

/** Body of `GET /jobs/{job_id}/template-items` — the QA-template
 *  allow-list a reviewer can pick from when adding a manual row. */
export interface TemplateItem {
  item_mp_id: number;
  item_mp_listing: string;
  category: string;
}

export interface TemplateItemsResponse {
  items: TemplateItem[];
}

/** One entry of `GET /jobs/{job_id}/item-mp` — the plugin's whole catalogue. */
export interface ItemMpMatch {
  item_mp_id: number;
  item_mp_text: string;
}

export interface ItemMpSearchResponse {
  items: ItemMpMatch[];
}

/** A catalogue value retrieved from the search service, echoed back on add.
 *  The unit label is resolved server-side from `quantity_id`. */
export interface SearchedMatch {
  key: string;
  /** Calibrated cosine, or `null` when the search service measured none. The
   *  mapping service derives the row's distance/score from it — and derives
   *  nothing at all when it is `null`, rather than substituting a stand-in. */
  similarity: number | null;
  /** Uncalibrated raw cosine, kept for reference. */
  raw_similarity?: number | null;
  /** What `similarity` is evidence of — see `ScoreBasis` in `@models/search`. */
  score_basis?: string | null;
  value_display: string;
  quantity_id?: number | null;
  value_id?: number | null;
  /** The search service's verdict on whether this candidate carries the typed
   *  value. It decides it over its whole retrieval pool; the mapping service
   *  raises the "not in RMI database" warning off it. */
  exact_value_match?: boolean | null;
}

/** Body of `POST /jobs/{job_id}/results`. `item_mp_text` + `matches` are set
 *  when the reviewer picked a catalogue field and the values were searched;
 *  `variant` identifies the TM vehicle variant for a manual row. */
export interface AddResultRowRequest {
  item_mp_id: number;
  value: string;
  item_mp_text?: string;
  matches?: SearchedMatch[];
  variant?: TmVariant | null;
  /** Search stages that fell back; withholds the server's "not in RMI"
   *  warning, which may not rest on evidence that was never gathered. */
  degraded?: string[];
}

export interface AddResultRowResponse {
  row: ResultRow;
}

/** One item_mp candidate from the wd index search, echoed back on add.
 *
 *  The wd counterpart of {@link SearchedMatch}, and the shapes differ because
 *  the searches answer opposite questions: TM pins the item and searches its
 *  VALUES (so every match shares one item_mp), wd searches the ITEMS (so each
 *  candidate is a different item_mp and brings its own id and label). */
export interface WdSearchedItem {
  item_mp_id: number;
  item_mp_text: string;
  /** `null` when the search service measured no cosine for this candidate —
   *  it still ranks (last) and is still pickable; it just carries no score. */
  similarity: number | null;
  main_group?: string | null;
  sub_group?: string | null;
  domain?: string | null;
}

/** One node of the wd taxonomy, as returned by `GET /jobs/{id}/taxonomy`. */
export interface TaxonomyGroupResponse {
  id: number;
  name: string;
  /** Sub groups only: the main group this hangs off, so the two dropdowns can
   *  cascade. */
  main_group_id?: number | null;
}

export interface WdTaxonomyResponse {
  main_groups: TaxonomyGroupResponse[];
  sub_groups: TaxonomyGroupResponse[];
}

export interface TaxonomyGroupViewModel {
  /** What a search filter must use — the index stores the id, not the name. */
  id: number;
  name: string;
  mainGroupId: number | null;
}

export interface WdTaxonomyViewModel {
  mainGroups: TaxonomyGroupViewModel[];
  subGroups: TaxonomyGroupViewModel[];
}

export interface TemplateItemViewModel {
  itemMpId: number;
  itemMpListing: string;
  category: string;
}

export interface ItemMpMatchViewModel {
  itemMpId: number;
  itemMpText: string;
}

// ---------- Catalogue item requests (WD `no_match` rows) -------------

/**
 * One missing catalogue entry, as the item-request mail presents it.
 *
 * The four `oe_*` fields are the source component in the customer's own words —
 * the same values the .xlsx export's first four columns carry, so a maintainer
 * can match the mail against the exported sheet. They were declared here as
 * `psa_*` and the API has always sent `oe_*`, so every one of them rendered as
 * the placeholder; the names now match what
 * `api_service/models/response/oe/item_requests.py` actually returns.
 */
export interface ItemRequestLine {
  row_index: number;
  oe_number: string | null;
  oe_name: string | null;
  oe_function: string | null;
  oe_domain: string | null;
  /** What the select LLM proposes the new entry be called. An AI suggestion,
   *  NOT a stored record — empty when nothing was retrieved for the row. */
  suggested_item_mp_text: string | null;
}

/** Body of `POST /jobs/{job_id}/item-requests/preview` — all fields optional. */
export interface ItemRequestPreviewRequest {
  /** Rows to include; omit for every row of the job still awaiting an entry.
   *  Named rows that are not misses are ignored, so the review table's current
   *  selection can be posted unfiltered. */
  row_indexes?: number[];
  /** Recipients; omit for the deployment's configured maintainer list. Must
   *  sit in the deployment's allowed domains — a 400 otherwise. */
  to?: string[];
  note?: string;
}

/**
 * The mail, before anyone sends it. Carries the message twice on purpose:
 * `subject` / `body` are what will be sent (and what the reviewer edits),
 * `lines` is the same content structured so a table can be rendered.
 */
export interface ItemRequestPreviewResponse {
  job_id: string;
  to: string[];
  subject: string;
  body: string;
  lines: ItemRequestLine[];
  row_count: number;
  /** The rows this mail covers. Echo it back as the send call's `row_indexes`
   *  so the sent message and its reported `row_count` describe the same set. */
  row_indexes: number[];
  /** Rows that matched before the 200-row per-request cap. */
  total_row_count: number;
  /** True when the cap bit: this mail covers only the first `row_count` of
   *  them, and the rest need a second request. */
  truncated: boolean;
  /** Name of the .xlsx the mail carries. The rows are NOT in `body` — the
   *  recipient creates catalogue entries, which is spreadsheet work; `lines`
   *  is the same data structured, for rendering the table on screen. */
  attachment_filename: string;
}

/** Body of `POST /jobs/{job_id}/item-requests`. */
export interface SendItemRequestRequest {
  row_indexes?: number[];
  to?: string[];
  cc?: string[];
  /** The reviewer's edited COVERING NOTE: sent VERBATIM. The rows are not in
   *  here — the attachment is always rebuilt from `row_indexes`, so editing
   *  this cannot change what data goes out. Omit both to have the server build
   *  the message the preview would have. */
  subject?: string;
  body?: string;
  /** Only used when the server builds the body. */
  note?: string;
}

export interface SendItemRequestResponse {
  job_id: string;
  message_id: string;
  to: string[];
  cc: string[];
  subject: string;
  row_count: number;
  row_indexes: number[];
  total_row_count: number;
  truncated: boolean;
  attachment_filename: string;
}

/** Body of `GET /jobs/{job_id}/result/export`. The server builds the .xlsx,
 *  uploads it, and returns a short-lived presigned download URL.
 *  `download_url` is null when the job has no result rows. */
export interface ResultExportResponse {
  download_url: string | null;
  filename: string;
  row_count: number;
}

// ---------- ViewModel types (camelCase, UI-shaped) ---------------------

export interface JobErrorViewModel {
  errorCode: string;
  message: string;
  description?: string;
}

/** Owner identity (camelCase, UI-shaped). */
export interface JobOwnerViewModel {
  userId: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
}

export interface JobFileViewModel {
  fileIndex: number;
  status: JobFile["status"];
  inputKind: JobInputKind;
  filename: string;
  memberPath: string | null;
  hasInputPdf: boolean;
  error: JobErrorViewModel | null;
  sourceUnits: JobFile["source_units"];
  rowsTotal: number;
  rowsOk: number;
  rowsFailed: number;
  rowsNoMatch: number;
  acceptedCount: number;
}

export interface JobViewModel {
  jobId: string;
  pluginId: string;
  processId?: string | null;
  files?: JobFileViewModel[];
  service?: string;
  status: JobStatus;
  inputKind: JobSourceKind;
  fileCount: number;
  filesDone: number;
  failedFiles: number;
  filename: string;
  rowsTotal: number | null;
  rowsOk: number;
  rowsFailed: number;
  rowsNoMatch: number;
  acceptedCount: number;
  error: JobErrorViewModel | null;
  owner?: JobOwnerViewModel | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Owner + date-range filter for the org-wide job list. Status/service/
 * plugin scoping is passed separately (they already exist on the page).
 * `userId` is what the backend filters on; `userEmail` is kept only so
 * the FilterPanel can render the selected user without a re-lookup.
 */
export interface JobListFilterViewModel {
  userId: string | null;
  userEmail: string | null;
  startDate: string | null;
  endDate: string | null;
  /** Job status to scope to; `"all"` means no status filter. */
  status: JobStatus | "all";
}

export interface CreateJobResponseViewModel {
  jobId: string;
  status: JobStatus;
  uploadUrl: string;
  uploadContentType: string;
  uploadExpiresAt: string;
  maxBytes: number;
}

export interface MatchInfoViewModel {
  rank: number;
  key: string;
  distance: number;
  similarity: number | null;
  /** Server-computed 0-100 headline review score: similarity (post
   *  golden/BM25 boost) blended with rerank confidence (+ canonical bonus
   *  where enabled). category is NOT a factor. null for hits written
   *  before the field existed. */
  score: number | null;
  /** 0-100 category ("key") match score. null when no category signal. */
  categoryScore: number | null;
  metadata: MatchMetadata;
}

export interface RowContextViewModel {
  label?: string;
  page?: number;
  bbox?: [number, number, number, number];
  conf?: number;
  [k: string]: unknown;
}

export interface ResultExportViewModel {
  downloadUrl: string | null;
  filename: string;
  rowCount: number;
}

export interface JobListViewModel {
  jobs: JobViewModel[];
  cursor: string | null;
}

/** Terminal notification — the page re-fetches the job + results on this. */
export interface MappingJobTerminalNotification {
  type: "mapping.job.completed" | "mapping.job.failed";
  jobId: string;
  pluginId: string;
  status: JobStatus;
  resultUrlPath: string;
}

/** Progress notification keeps file-level source units separate from result rows. */
export interface MappingJobProgressNotification {
  type: "mapping.job.progress";
  jobId: string;
  status: JobStatus;
  fileIndex?: number;
  sourceUnits?: { kind: string; total: number; done: number };
  rowsOk: number;
  rowsFailed: number;
  rowsTotal: number;
}

export type MappingJobNotification =
  | MappingJobTerminalNotification
  | MappingJobProgressNotification;

// ---------- Helpers ----------------------------------------------------

const EXTENSION_TO_INPUT_KIND: Readonly<Record<string, JobInputKind>> = {
  json: "json",
  csv: "csv",
  xlsx: "excel",
  txt: "text",
  zip: "zip",
};

export const detectInputKind = (filename: string): JobInputKind | null => {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  return EXTENSION_TO_INPUT_KIND[ext] ?? null;
};

/**
 * Default upload cap enforced server-side (see
 * `mapping_core.DEFAULT_MAX_INPUT_BYTES`). Mirrored here so the
 * frontend can reject oversize files before issuing POST /jobs.
 * `POST /jobs` returns the authoritative cap in `maxBytes`; we
 * still rely on that response for a server-truth second check.
 */
export const DEFAULT_MAX_INPUT_BYTES = 50 * 1024 * 1024;

/**
 * Per-row review state — what a REVIEWER did.
 *
 * `Failed` is a DISPLAY value only. The backend stopped writing it to
 * `review_status` (a row the pipeline could not map was never reviewed, so it
 * is `pending` there); the result-row builder folds
 * `mapping_status === "failed"` back into this field so the Status column,
 * its filter chip and the disabled row actions keep working off one value.
 * It is never sent back in a PATCH — the API type rejects it.
 */
export const ReviewStatus = {
  Pending: "pending",
  Accepted: "accepted",
  Rejected: "rejected",
  Corrected: "corrected",
  Failed: "failed",
} as const;

export type ReviewStatus = (typeof ReviewStatus)[keyof typeof ReviewStatus];

/**
 * What a REVIEWER may actually set — every status except `Failed`.
 *
 * `Failed` is a display value the client folds in from `mapping_status` so the
 * Status column reads one field; the API rejects it, because a row the
 * pipeline could not map was never reviewed. Deriving the send-side type by
 * subtraction is what makes that unsendable rather than merely documented: a
 * `RowEdit` carrying `Failed` no longer compiles, so the rule cannot be broken
 * by a new call site the way a runtime guard could be.
 */
export type ReviewAction = Exclude<ReviewStatus, typeof ReviewStatus.Failed>;

/**
 * `Omit` that keeps a union a union.
 *
 * The built-in `Omit<A | B, K>` collapses its argument into ONE object type
 * with the merged (and therefore all-optional) keys, which quietly throws away
 * the discrimination `RowEdit` exists to enforce: `Omit<RowEdit, "editedAt">`
 * accepts `{corrected}` with no rank, the exact shape the API rejects.
 * Distributing over the members preserves it.
 */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

/** Machine verdict, independent of review state. */
export const MappingStatus = {
  Failed: "failed",
  NoMatch: "no_match",
  AutoAccepted: "auto_accepted",
  Matched: "matched",
} as const;

export type MappingStatus = (typeof MappingStatus)[keyof typeof MappingStatus];

/**
 * In-memory edit a reviewer has applied to a row, before the PATCH lands.
 *
 *  - accept what the row points at (`Accepted`, no rank needed — an accept
 *    without one SEALS the stored pick rather than reverting to the top)
 *  - replace it with another candidate (`Corrected`, `chosenRank` REQUIRED —
 *    a correction IS a pick, including picking rank 1 back to undo one)
 *  - reject everything (`Rejected`)
 *
 * The rank/status pairing is expressed as a union, not as optional fields on
 * one shape: `Corrected` without a rank is the combination the API rejects,
 * and there is no reason for it to be constructible here.
 */
/** A reviewer's verdict before it is timestamped — what callers pass in. */
export type RowEditInput = DistributiveOmit<RowEdit, "editedAt">;

export type RowEdit = {
  /** Edited source value to persist alongside the verdict (inline value
   *  edit). Independent of the verdict — it changes the row's INPUT, not
   *  which candidate it points at. */
  text?: string;
  editedAt: string;
} & (
  | { reviewStatus: typeof ReviewStatus.Corrected; chosenRank: number }
  | {
      reviewStatus: typeof ReviewStatus.Accepted;
      /** Optional: naming one re-seals a different candidate. */
      chosenRank?: number;
    }
  | {
      reviewStatus:
        | typeof ReviewStatus.Pending
        | typeof ReviewStatus.Rejected;
      chosenRank?: never;
    }
);

/** `GET /plugins/oe/input-template` — the empty workbook, as JSON.
 *
 *  Base64 rather than a binary body: passing an .xlsx through API Gateway
 *  untouched needs `binaryMediaTypes` configured, and nothing is stored to
 *  presign because the file is the same for every caller.
 *
 *  `columns` is the accepted header row. It ships so the UI can name the
 *  columns without keeping its own copy — which is how the result-table labels
 *  went stale against the fields they headed. */
export interface InputTemplateResponse {
  filename: string;
  columns: string[];
  content_base64: string;
}
